import { inject, Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import jwtDecode from 'jwt-decode';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

interface AuthRequestBody {
  username: string;
  password: string;
}

export interface UserDetailsResponse {
  passwordChangeDone: string;
  password_dt: string;
  activeFlg: string;
  firstName: string;
  lastName: string;
  username: string;
  language: string;
}

interface JwtToken {
  iss: string;
  sub: string;
  exp: number;
  roles: string[];
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private readonly httpClient: HttpClient;

  public handler = inject(HttpBackend);
  constructor() {
    this.httpClient = new HttpClient(this.handler);
  }
  async validateCredentials(username: string, password: string, dealerId: string, customerCode: string): Promise<UserDetailsResponse> {
    const requestBody: AuthRequestBody = { username, password };
    const url = `${environment.authUrl}${environment.serviceUris.authBase}${dealerId}/token`;

    const response = await firstValueFrom(this.httpClient.post<UserDetailsResponse>(url, requestBody, { observe: 'response' }));
    const status = response.status;
    const token = response.headers.get('Authorization');
    if (status === 200 && token) {
      const decodedToken = jwtDecode<JwtToken>(token);

      localStorage.setItem('token', token);
      localStorage.setItem('userId', username);
      localStorage.setItem('dealerId', dealerId);
      localStorage.setItem('token-expiry', decodedToken.exp.toString());
      localStorage.setItem('customer', customerCode);
      localStorage.setItem('roles', JSON.stringify(decodedToken.roles));
      localStorage.setItem('firstName', response.body?.firstName ?? '');
      localStorage.setItem('lastName', response.body?.lastName ?? '');
    }
    return response.body;
  }

  async changePassword(
    username: string,
    password: string,
    dealerId: string,
    token: string,
    oldPassword: string,
    loggedInUsername: string
  ): Promise<void> {
    const requestBody = { username, password, oldPassword, loggedInUsername };
    const url = `${environment.authUrl}${environment.serviceUris.authBase}${dealerId}/changePassword`;

    await firstValueFrom(
      this.httpClient.post(url, requestBody, {
        headers: {
          'Authorization': `Bearer ${token}`, // Add token to headers
          'Content-Type': 'application/json' // Specify content type
        }
      })
    );
  }

  isAuthenticated() {
    return localStorage.getItem('token') !== null;
  }

  /** Single source of truth for token-expiry evaluation.
   *  Returns true when the token is missing or its expiry timestamp has passed.
   *  Used by both the HTTP interceptor and any component-level session checks.
   */
  isTokenExpired(): boolean {
    const token = localStorage.getItem('token');
    const expiry = localStorage.getItem('token-expiry');
    return !token || !expiry || Date.now() >= Number(expiry) * 1000;
  }

  async encryptPassword(password: string, newHashEnabled: string): Promise<string> {
    const msgUint8 = new TextEncoder().encode(password);
    let hashBuffer = await crypto.subtle.digest('SHA-1', msgUint8);

    // if newHashEnabled is "Y", then do digest with SHA-256
    if (newHashEnabled.trim().toUpperCase() === 'Y') {
      const sha1HashArray = Array.from(new Uint8Array(hashBuffer));
      const sha1Hex = sha1HashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
      // Convert sha1Hex to Uint8Array
      const sha1HexUint8 = new TextEncoder().encode(sha1Hex);
      hashBuffer = await crypto.subtle.digest('SHA-256', sha1HexUint8);
    }

    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  getRoles() {
    return localStorage.getItem('roles') !== null ? localStorage.getItem('roles') : '';
  }

  /**
   * Decodes the JWT stored in localStorage and returns the roles array from the token payload.
   * Reading from the token (rather than the separate 'roles' key) prevents localStorage tampering.
   */
  getRolesFromToken(): string[] {
    try {
      const rawToken = localStorage.getItem('token');
      if (!rawToken) return [];
      const token = rawToken.startsWith('Bearer ') ? rawToken.slice(7) : rawToken;
      const decoded = jwtDecode<JwtToken>(token);
      return Array.isArray(decoded.roles) ? decoded.roles : [];
    } catch {
      return [];
    }
  }
}
